/**
 * Safely parses an array from localStorage.
 * If the item does not exist, or is not a valid JSON array, returns the defaultValue.
 * 
 * @param {string} key - The localStorage key to retrieve.
 * @param {Array} defaultValue - The default value to return if parsing fails or result is not an array.
 * @returns {Array} - The parsed array or the default value.
 */
export const safeParseArray = (key, defaultValue = []) => {
    try {
        const item = localStorage.getItem(key);
        if (!item) return defaultValue;

        const parsed = JSON.parse(item);
        if (Array.isArray(parsed)) return parsed;

        console.warn(`[Storage] Key "${key}" contained non-array data. Resetting to default.`);
        localStorage.setItem(key, JSON.stringify(defaultValue)); // Auto-heal
        return defaultValue;
    } catch (error) {
        console.error(`[Storage] Error parsing "${key}":`, error);
        localStorage.setItem(key, JSON.stringify(defaultValue)); // Auto-heal
        return defaultValue;
    }
};

/**
 * Safely parses an object from localStorage.
 * If the item does not exist, or is not a valid JSON object, returns the defaultValue.
 * 
 * @param {string} key - The localStorage key to retrieve.
 * @param {Object} defaultValue - The default value to return if parsing fails.
 * @returns {Object} - The parsed object or the default value.
 */
export const safeParseObject = (key, defaultValue = {}) => {
    try {
        const item = localStorage.getItem(key);
        if (!item) return defaultValue;

        const parsed = JSON.parse(item);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed;
        }

        console.warn(`[Storage] Key "${key}" contained non-object data. Resetting to default.`);
        localStorage.setItem(key, JSON.stringify(defaultValue)); // Auto-heal
        return defaultValue;
    } catch (error) {
        console.error(`[Storage] Error parsing "${key}":`, error);
        localStorage.setItem(key, JSON.stringify(defaultValue)); // Auto-heal
        return defaultValue;
    }
};
